import React, {useState, useCallback, useEffect} from 'react';
import {ScrollView, View} from "react-native";
import EStyleSheet from "react-native-extended-stylesheet";

import Modal from "../../../../../../components/Modal";
import ContentSection from "../../../../../../components/ContentSection";
import AppText from "../../../../../../components/AppText";
import {textStyles} from "../../../../../../styles/text";
import AppTextInput from "../../../../../../components/AppTextInput";
import {basicStyles} from "../../../../../../styles/basic";
import AppButton from "../../../../../../components/AppButton";
import AppPickerInput from "../../../../../../components/AppPickerInput";

function ConditionModal({onClose, onSubmit, conditionOperators, condition = null, isEdit = false, automationDefs = null}) {
    const [conditionData, setConditionData] = useState({
        message_automation_condition_type_id: automationDefs?.conditionTypes?.[0]?.message_automation_condition_type_id?.toString() || '',
        message_automation_condition_operator_id: automationDefs?.conditionOperators?.[0]?.message_automation_condition_operator_id?.toString() || '',
        message_automation_condition_operator_slug: automationDefs?.conditionOperators?.[0]?.message_automation_condition_operator_slug || '',
        message_automation_condition_value: '',
        message_automation_condition_logical_operator_id: '1',
    });

    const updateConditionData = useCallback((prop, value) => {
        setConditionData(current => {
            let update = {...current};
            update[prop] = value;

            if (prop === 'message_automation_condition_operator_id') {
                const conditionOperator = conditionOperators.find(
                    op => op.message_automation_condition_operator_id.toString() === value.toString()
                );
                update['message_automation_condition_operator_slug'] = conditionOperator?.message_automation_condition_operator_slug || '';
            }

            if (prop === 'message_automation_condition_type_id') {
                const conditionType = automationDefs?.conditionTypes?.find(
                    type => type.message_automation_condition_type_id.toString() === value.toString()
                );

                if (conditionType?.message_automation_condition_type_slug === 'conversation-state') {
                    const exactMatchOperator = conditionOperators.find(
                        operator => operator.message_automation_condition_operator_slug === '='
                    );
                    update.message_automation_condition_operator_id = exactMatchOperator?.message_automation_condition_operator_id?.toString() || '';
                    update.message_automation_condition_operator_slug = exactMatchOperator?.message_automation_condition_operator_slug || '=';
                    update.message_automation_condition_value = ['new', 'open', 'closed'].includes(update.message_automation_condition_value)
                        ? update.message_automation_condition_value
                        : 'new';
                }
            }

            return update;
        });
    }, [automationDefs, conditionOperators]);

    const handleClose = useCallback(() => {
        onClose();
        setConditionData({
            message_automation_condition_type_id: '',
            message_automation_condition_operator_id: '',
            message_automation_condition_value: '',
            message_automation_condition_logical_operator_id: automationDefs?.logicalOperators?.find(op => op.message_automation_condition_logical_operator_slug === 'and')?.message_automation_condition_logical_operator_id?.toString() || '1',
            message_automation_condition_operator_slug: automationDefs?.conditionOperators?.[0]?.message_automation_condition_operator_slug || '',
        });
    }, [onClose, automationDefs]);

    const handleSubmit = useCallback(() => {
        onSubmit(conditionData);
        handleClose();
    }, [conditionData, onSubmit, handleClose]);

    useEffect(() => {
        if (isEdit && condition) {
            setConditionData(condition);
        } else if (!isEdit && automationDefs?.conditionOperators?.[0]) {
            // Set default operator and logical operator for new conditions
            setConditionData(current => ({
                ...current,
                message_automation_condition_operator_id: automationDefs.conditionOperators[0].message_automation_condition_operator_id.toString(),
                message_automation_condition_logical_operator_id: automationDefs.logicalOperators?.find(op => op.message_automation_condition_logical_operator_slug === 'and')?.message_automation_condition_logical_operator_id?.toString() || '1'
            }));
        }
    }, [isEdit, condition, automationDefs]);

    const selectedConditionType = (
        automationDefs?.conditionTypes?.find(
            type => type.message_automation_condition_type_id.toString() === conditionData.message_automation_condition_type_id?.toString()
        )
    );
    const isConversationState = selectedConditionType?.message_automation_condition_type_slug === 'conversation-state';

    return (
        <Modal
            active={true}
            onClose={handleClose}>
            <ContentSection
                style={[styles.contentWrapper]}
                onClick={e => {
                    e.stopPropagation();
                }}
            >
                <ScrollView>
                    <AppText style={[textStyles.pageTitle, {marginBottom: 20}]}>
                        {isEdit ? 'Edit' : 'Add New'} Condition
                    </AppText>

                    <AppPickerInput
                        label="Field"
                        selectedValue={conditionData.message_automation_condition_type_id?.toString() || ''}
                        onValueChange={(value) => updateConditionData('message_automation_condition_type_id', value)}
                        items={automationDefs?.conditionTypes?.map(conditionType => ({
                            label: conditionType.message_automation_condition_type_title,
                            value: conditionType.message_automation_condition_type_id.toString()
                        })) || []}
                        labelExtractor="label"
                        valueExtractor="value"
                        wrapperStyle={styles.inputWrapper}
                    />

                    {!isConversationState && (
                        <AppPickerInput
                            label={'Condition'}
                            selectedValue={conditionData.message_automation_condition_operator_id?.toString() || ''}
                            onValueChange={value => {
                                updateConditionData('message_automation_condition_operator_id', value)
                            }}
                            items={conditionOperators}
                            labelExtractor={'message_automation_condition_operator_title'}
                            valueExtractor={item => item.message_automation_condition_operator_id.toString()}
                            wrapperStyle={styles.inputWrapper}
                        />
                    )}

                    {isConversationState ? (
                        <AppPickerInput
                            label="Value"
                            selectedValue={conditionData.message_automation_condition_value}
                            onValueChange={(value) => updateConditionData('message_automation_condition_value', value)}
                            items={[
                                {label: 'New', value: 'new'},
                                {label: 'Open', value: 'open'},
                                {label: 'Closed', value: 'closed'},
                            ]}
                            labelExtractor="label"
                            valueExtractor="value"
                            wrapperStyle={styles.inputWrapper}
                        />
                    ) : (
                        <AppTextInput
                            label="Value"
                            value={conditionData.message_automation_condition_value}
                            onChangeText={(value) => updateConditionData('message_automation_condition_value', value)}
                            wrapperStyle={styles.inputWrapper}
                            placeholder="Enter the value to match"
                        />
                    )}

                    <View
                        style={[basicStyles.flexRow, basicStyles.justifyContentSpaceBetween, basicStyles.alignContentCenter, {marginTop: 20}]}>
                        <View style={[basicStyles.flexRow]}>
                            <AppButton
                                label="Cancel"
                                action={handleClose}
                                style={{marginRight: 10}}
                                theme="transBlue"
                            />
                            <AppButton
                                label="Save Condition"
                                action={handleSubmit}
                            />
                        </View>
                    </View>
                </ScrollView>
            </ContentSection>
        </Modal>
    );
}

export default ConditionModal;

const styles = EStyleSheet.create({
    contentWrapper: {
        padding: 32,
        width: 500,
        maxWidth: '100%',
        maxHeight: '100vh'
    },
    inputWrapper: {
        marginBottom: 10
    }
});
